import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'tss-react/mui';
import { compareAsc } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers';
import { IconButton, TextField, InputLabel, MenuItem } from '@mui/material';
import Search from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import Card from '../common/Card';
import ActionType from 'src/enums/actionType';
import { useTheme } from '@emotion/react';

const typeOptions = [
  ActionType.SMS,
  ActionType.EMAIL,
  ActionType.MEMBER_NOTIFICATION,
  ActionType.PUSH_XPLORACTIVE,
  ActionType.XPLORACTIVE_NEWS,
].map(actionType => ({
  label: actionType.name,
  value: actionType.id,
}));

const configIsValid = cfg => {
  let isValid = true;
  isValid = !!cfg.type;
  if (isValid && (!cfg.to || !cfg.from)) isValid = false;
  if (isValid) isValid = compareAsc(cfg.from, cfg.to) < 0;
  return isValid;
};

const useStyles = makeStyles()(theme => ({
  popover: { padding: 0, margin: 0 },
  menuItem: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    outline: 'none',
    cursor: 'pointer',
    padding: '9px 3px',
    textAlign: 'center',
    '&:hover': {
      ...theme.typography.body2,
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      opacity: 0.9,
    },
  },
}));

const StyledSelect = ({ options, ...others }) => {
  const { classes } = useStyles();
  return (
    <TextField
      fullWidth
      select
      style={{ flex: 1 }}
      SelectProps={{
        MenuProps: {
          MenuListProps: { style: { padding: 0, margin: 0 } },
          PopoverClasses: {
            paper: classes.popover,
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          marginThreshold: 0,
        },
      }}
      {...others}
    >
      {options.map(o => (
        <MenuItem key={o.label} value={o.value} className={classes.menuItem}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

StyledSelect.propTypes = {
  options: PropTypes.array.isRequired,
};

function Config({ apply, cfg, setCfg }) {
  const { t } = useTranslation();
  function updateCfg(value, field) {
    setCfg(currentConfig => ({ ...currentConfig, [field]: value }));
  }
  const theme = useTheme();
  return (
    <div style={{ width: '100%' }}>
      <Card title={t('smartmessaging.sentMessages.configTitle')}>
        <div style={{ display: 'flex', marginTop: '12px' }}>
          <div style={{ margin: '0 4px', display: 'flex' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                margin: '0 4px',
              }}
            >
              <InputLabel htmlFor="type">{t('smartmessaging.sentMessages.messageType')}</InputLabel>
            </div>
            <div style={{ flex: 1 }}>
              <StyledSelect
                id="type"
                options={typeOptions}
                onChange={e => {
                  updateCfg(e.target.value, 'type');
                }}
                value={cfg.type || ''}
              />
            </div>
          </div>
          <div style={{ margin: '0 4px', display: 'flex' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                margin: '0 4px',
              }}
            >
              <InputLabel htmlFor="from">
                {t('smartmessaging.campaignList.filterLabel.creationDate.from')}
              </InputLabel>
            </div>
            <div style={{ flex: 1 }}>
              <DatePicker
                closeOnSelect
                id="from"
                onChange={date => {
                  updateCfg(date, 'from');
                }}
                format="dd/MM/yyyy"
                value={cfg.from}
                slotProps={{
                  actionBar: {
                    actions: ['clear'],
                  },
                }}
              />
            </div>
          </div>
          <div style={{ margin: '0 4px', display: 'flex' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                margin: '0 4px',
              }}
            >
              <InputLabel htmlFor="to">
                {t('smartmessaging.campaignList.filterLabel.creationDate.to')}
              </InputLabel>
            </div>
            <div style={{ flex: 1 }}>
              <DatePicker
                closeOnSelect
                onChange={date => {
                  updateCfg(date, 'to');
                }}
                format="dd/MM/yyyy"
                value={cfg.to}
                slotProps={{
                  actionBar: {
                    actions: ['clear'],
                  },
                }}
              />
            </div>
          </div>
          <div>
            <IconButton
              disabled={!configIsValid(cfg)}
              onClick={e => {
                e.preventDefault();
                apply();
              }}
              size="large"
            >
              <Search style={{ fill: theme.palette.text.secondary }} />
            </IconButton>
          </div>
        </div>
      </Card>
    </div>
  );
}

Config.propTypes = {
  apply: PropTypes.func.isRequired,
  cfg: PropTypes.object.isRequired,
  setCfg: PropTypes.func.isRequired,
};

export default Config;
